import { Route, Routes } from 'react-router-dom';
import ChatWindow from './pages/ChatWindow.jsx';
import TagManager from 'react-gtm-module';
import React, { useState, useEffect } from 'react';
import './services/i18n';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga4';
import axios from 'axios';
import Loader from './components/Loader.jsx';
import toast, { Toaster } from 'react-hot-toast';

const gtmID = process.env.REACT_APP_GTM_ID;

const tagManagerArgs = {
    gtmId: gtmID
}

// TagManager.initialize(tagManagerArgs);

const App = () => {

    const { t, i18n } = useTranslation();

    const ASSETS_URL = process.env.REACT_APP_ASSETS_URL;
    const apiURL = process.env.REACT_APP_API_URL;
    const apiKey = process.env.REACT_APP_BOT_API_KEY;
  
    const [verified, setVerified] = useState(false);
    const [iframeDomain, setIframeDomain] = useState('');
    const [view, setView] = useState('narrow');
    const [domains, setDomains] = useState(null);
    const [domainFromIframeMessage, setDomainFromIframeMessage] = useState();
    const [publicEnabled, setPublicEnabled] = useState(false);
    const [widgetEnabled, setWidgetEnabled] = useState(false);
    const [isInIframe, setIsInIframe] = useState(false);
    const [loader, setLoader] = useState(true);
    const [botResponse, setBotResponse] = useState(null);
    const [error, setError] = useState(false);

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const botId = urlParams?.get("bot");
    const language = urlParams?.get('language');
    const color1 = urlParams?.get('primary');
    const defaultView = urlParams?.get('view');
    const showDisclaimer = urlParams?.get('showDisclaimer') ? urlParams?.get('showDisclaimer') === 'true' : true;
    const domain = urlParams?.get('domain') || null;
  
    const showVoiceMode = urlParams?.get('voiceMode') ? urlParams?.get('voiceMode') === 'true' : false;
  
    const localLanguage = localStorage.getItem('language');
  
    const handlePostMessage = (event) => {
  
      if(event.origin.includes(domain) || domain == 'localhost:3000') {
        setDomainFromIframeMessage(event?.data?.domain ? event?.data?.domain : domain);
      }
  
      if(event.origin.includes(domain) || domain == 'localhost:3000') {
        if(event.data.view){
          setView(event.data.view)
        }
  
        if(event.data.chatId){
          localStorage.setItem(`chatId_${botId}`, event.data.chatId);
        }
  
        if(event.data.clearChat){
          localStorage.removeItem(`chatId_${botId}`);
          localStorage.removeItem('language');
          localStorage.removeItem(`initialLanguageSet_${botId}`);
        }
      }
    };
  
    const fetchDomains = async () => {
        if(!isInIframe || !widgetEnabled) return;
                
        await axios({
            url: `${ASSETS_URL}/api/widget-domains`,
            method: 'GET'
        }).then((response) => {
            if(response.status == 200){
    
            const whitelistedDomains = response.data.data;
    
            setDomains(whitelistedDomains);
    
            for(let i = 0; i < whitelistedDomains.length; i++){
                if((whitelistedDomains[i].attributes.Domain == domain && whitelistedDomains[i].attributes.Domain == domainFromIframeMessage) || (whitelistedDomains[i].attributes.Domain == '*')){
                setIframeDomain(domain);
                setVerified(true);

                if(!loader){
                    setLoader(false);
                }
                }
            }
            }
    
        }).catch((err) => {
            console.log("Error fetching domains ======>", err);
            setError(true);
        })
    }
  
    const fetchInfo = async () => {
        if(!botId) return;
    
        let chat_id = localStorage.getItem(`chatId_${botId}`);
        let language = localStorage.getItem('language') || 'en';

        let requestData = {
            domain: domain || '',
            language: language || 'en'
        }

        if (chat_id) {
            requestData.chat_id = chat_id
        }

        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
    
        const customVars = {};
        for (const [key, value] of urlParams.entries()) {
            if (key.startsWith('cv_')) {
                const decodedKey = decodeURIComponent(key.substring(3));
                const decodedValue = decodeURIComponent(value);
                customVars[decodedKey] = decodedValue;
            }
        }

        if(customVars){
            requestData['custom_variables'] = customVars;
        }
  
        axios({
            url: `${apiURL}/bot_by_id/bot_${botId}`,
            method: 'POST',
            data: requestData,
            headers: {
            'Api-token': apiKey
            }
        })
        .then((response) => {
          if (response.status === 200 && response.data.data.length > 0) {
            setBotResponse(response);
            setPublicEnabled(response.data.data[0].attributes.enable_chat_page);
            // setWidgetEnabled(response.data.data[0].attributes.enable_chat_widget);
            setWidgetEnabled(true);

            ReactGA.initialize(response.data.data[0].attributes.measurement_id || process.env.REACT_APP_MEASUREMENT_ID);

            if(!loader && !isInIframe){
                setLoader(false);
            }
          }
        })
        .catch((err) => {
            console.log(err);
            setError(true);
            toast.error(t('Unable to load at the moment! Please try later.'), {
                position: 'top-right',
                style: {
                backgroundColor: 'white',
                color: 'red',
                fontSize: '13px',
                padding: '5px 7px',
                fontWeight: '500'
                }
            })
        })
    };
  
    useEffect(() => {
      if(domainFromIframeMessage) fetchDomains();
    }, [domainFromIframeMessage, widgetEnabled, publicEnabled])
  
    useEffect(() => {
  
        setView(defaultView);
    
        fetchInfo();
    
        setIsInIframe(window.self !== window.top);
    
        window.addEventListener('message', handlePostMessage);
    
        return () => {
            window.removeEventListener('message', handlePostMessage);
        };
    }, []);
  
    useEffect(() => {
  
        i18n.changeLanguage(localLanguage ? localLanguage : language ? language : "en");
    
        if(!localLanguage){
            localStorage.setItem('language', language ? language : 'en')
        }
  
    }, [language])
  
    // For deployed site
    if(((publicEnabled && !isInIframe) || (verified && widgetEnabled)) && botId && botId !== "null"){
        return (
            <Routes>
                <Route path='/' element={<ChatWindow botResponse={botResponse} iframeDomain={iframeDomain} botApiId={botId} view={view} primaryColor={color1 ? `#${color1}` : '#263FA9'} showDisclaimer={showDisclaimer} showVoiceMode={showVoiceMode} publicMode={publicEnabled} />} />
            </Routes>
        )
    }

    // For local site
    // if(botResponse){
    //     return (
    //         <Routes>
    //             <Route path='/' element={<ChatWindow botResponse={botResponse} iframeDomain={'localhost:3000'} botApiId='eb221357_ffca_412d_8909_66733ca02799' view={view} primaryColor={color1 ? `#${color1}` : '#912d2a'} showVoiceMode={true} />} />
    //         </Routes>
    //     )
    // }

    return (
        <>
            {
                !error && (
                    <Loader primary={color1 ? `#${color1}` : '#263FA9'} showLoader={loader} />
                )
            }
            <Toaster />
        </>
    );

}

export default App;